import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, ShieldCheck } from 'lucide-react';

import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AccountingModal } from '@/components/accounting/AccountingModal';
import { isDesktopApp } from '@/lib/desktop';
import {
  passkeyStepUp,
  useSwitchToTenant,
  type SecondFactorMethod,
  type SwitchStepUp,
} from '@/api/queries/firm';

/** Profile page that holds the two-factor and passkey sections. */
export const SECOND_FACTOR_ENROL_PATH = '/profile';

export interface SwitchTarget {
  tenantId: string;
  name: string;
}

type Step = 'confirm' | 'second-factor' | 'not-enrolled';

function methodsOf(err: ApiError): SecondFactorMethod[] {
  const raw = err.details?.['methods'];
  if (!Array.isArray(raw)) return ['TOTP'];
  const methods = raw.filter((m): m is SecondFactorMethod => m === 'TOTP' || m === 'PASSKEY');
  return methods.length > 0 ? methods : ['TOTP'];
}

function canUsePasskey(): boolean {
  return !isDesktopApp() && typeof window !== 'undefined' && !!window.PublicKeyCredential;
}

/**
 * Confirms a switch of client file, naming the target, then collects the
 * second factor the API asks for (code or passkey). A user with none
 * enrolled is sent to the profile page to enrol one. `keepPage` keeps the
 * current page and cache (see `useSwitchToTenant`).
 */
export function SwitchTenantDialog({
  target,
  onClose,
  keepPage = false,
}: {
  target: SwitchTarget | null;
  onClose: () => void;
  keepPage?: boolean;
}) {
  const switchTo = useSwitchToTenant();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('confirm');
  const [methods, setMethods] = useState<SecondFactorMethod[]>([]);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const codeRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setStep('confirm');
    setMethods([]);
    setCode('');
    setError(null);
    setBusy(false);
  }, [target?.tenantId]);

  if (!target) return null;

  async function attempt(stepUp?: SwitchStepUp) {
    if (!target) return;
    setBusy(true);
    setError(null);
    try {
      if (keepPage) await switchTo(target.tenantId, stepUp, { keepPage: true });
      else await switchTo(target.tenantId, stepUp);
      onClose();
    } catch (err) {
      setBusy(false);
      if (!(err instanceof ApiError)) {
        setError('La bascule de dossier a échoué.');
        return;
      }
      if (err.code === 'SECOND_FACTOR_NOT_ENROLLED') {
        setError(err.message);
        setStep('not-enrolled');
        return;
      }
      if (err.code === 'CONFIRMATION_RENFORCEE_REQUISE') {
        setMethods(methodsOf(err));
        setStep('second-factor');
        return;
      }
      if (err.code === 'CONFIRMATION_RENFORCEE_INVALIDE') {
        setMethods(methodsOf(err));
        setCode('');
      }
      setError(err.message);
    }
  }

  async function presentPasskey() {
    setBusy(true);
    setError(null);
    let proof: SwitchStepUp;
    try {
      proof = await passkeyStepUp();
    } catch (err) {
      setBusy(false);
      setError(
        err instanceof ApiError
          ? err.message
          : (err as { name?: string })?.name === 'NotAllowedError'
            ? 'Opération annulée.'
            : "La clé d'accès n'a pas pu être utilisée."
      );
      return;
    }
    await attempt(proof);
  }

  function submitCode(e: FormEvent) {
    e.preventDefault();
    if (code.trim().length === 0) return;
    void attempt({ method: 'TOTP', code: code.trim() });
  }

  const offersTotp = methods.includes('TOTP');
  const offersPasskey = methods.includes('PASSKEY') && canUsePasskey();

  return (
    <AccountingModal
      open
      title="Changer de dossier"
      onClose={onClose}
      initialFocus={step === 'second-factor' && offersTotp ? codeRef : undefined}
      footer={
        step === 'confirm' ? (
          <>
            <Button variant="outline" onClick={onClose} disabled={busy}>
              Annuler
            </Button>
            <Button onClick={() => void attempt()} disabled={busy}>
              {busy ? 'En cours…' : 'Passer au dossier'}
            </Button>
          </>
        ) : step === 'not-enrolled' ? (
          <>
            <Button variant="outline" onClick={onClose}>
              Annuler
            </Button>
            <Button
              onClick={() => {
                onClose();
                navigate(SECOND_FACTOR_ENROL_PATH);
              }}
            >
              Activer la double authentification
            </Button>
          </>
        ) : (
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Annuler
          </Button>
        )
      }
    >
      {step === 'confirm' && (
        <p className="text-sm text-slate-700">
          Passer au dossier <strong className="font-semibold text-slate-900">{target.name}</strong> ?
        </p>
      )}

      {step === 'not-enrolled' && (
        <div className="flex items-start gap-3">
          <ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-lagune-700" aria-hidden="true" />
          <p className="text-sm text-slate-700">{error}</p>
        </div>
      )}

      {step === 'second-factor' && (
        <div className="space-y-4">
          <p className="text-sm text-slate-700">
            Confirmez votre identité pour ouvrir le dossier{' '}
            <strong className="font-semibold text-slate-900">{target.name}</strong>.
          </p>
          {offersTotp && (
            <form onSubmit={submitCode} className="space-y-2">
              <Label htmlFor="switch-step-up-code">Code de vérification</Label>
              <div className="flex flex-wrap gap-2">
                <Input
                  id="switch-step-up-code"
                  ref={codeRef}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={32}
                  className="min-w-0 flex-1"
                  disabled={busy}
                />
                <Button type="submit" disabled={busy || code.trim().length === 0}>
                  {busy ? 'En cours…' : 'Valider'}
                </Button>
              </div>
              <p className="text-xs text-slate-600">
                Code à 6 chiffres de votre application d'authentification, ou un code de secours.
              </p>
            </form>
          )}
          {offersPasskey && (
            <Button
              type="button"
              variant="outline"
              onClick={() => void presentPasskey()}
              disabled={busy}
              className="w-full"
            >
              <KeyRound className="mr-2 h-4 w-4" aria-hidden="true" />
              Utiliser ma clé d'accès
            </Button>
          )}
          {!offersTotp && !offersPasskey && (
            <p className="text-sm text-slate-700">
              Votre clé d'accès n'est pas utilisable sur cet appareil.
            </p>
          )}
        </div>
      )}

      {error && step !== 'not-enrolled' && (
        <p role="alert" className="mt-3 text-sm text-red-700">
          {error}
        </p>
      )}
    </AccountingModal>
  );
}

export default SwitchTenantDialog;
